<template>
  <div class="md:border rounded-xl md:p-6 sticky top-24">
    <div
      class="flex flex-row items-center justify-start gap-2 border-b border-gray-300 pb-6"
    >
      <h4 class="font-sans text-xl font-bold">
        {{ $t('YourOrder') }}
      </h4>
      <span
        class="rounded-full bg-blue bg-opacity-5 text-blue font-sans font-bold text-sm px-2 h-6 min-w-[1.5rem] flex items-center justify-center"
        >{{ selectedValidPapers.length }}</span
      >
    </div>
    <FormAlert v-if="!allowTrial" class="mt-4 whitespace-pre-wrap" type="info"
      >{{ $t('basketContainsPaperInWaitingPeriod') }}
    </FormAlert>
    <div>
      <template v-if="config.public.site.allowPurchase">
        <SubscriptionsOrderLine
          v-for="(paper, index) in selectedValidPapers"
          :key="paper"
          :name="papers.find((p) => p.RecordId === paper)?.Name ?? ''"
          :amount-per-year="5988"
          :discount="index === 0 ? 0 : discount"
          @remove="
            removeSelectedPaper(
              papers.find((p) => p.RecordId === paper) as ContentPaper
            )
          "
        />
      </template>
      <template v-else>
        <SubscriptionsOrderLineCompact
          v-for="paper in selectedValidPapers"
          :key="paper"
          :name="papers.find((p) => p.RecordId === paper)?.Name ?? ''"
          :inWaitingPeriod="Boolean(papersInWaitingPeriod?.includes(paper))"
          @remove="
            removeSelectedPaper(
              papers.find((p) => p.RecordId === paper) as ContentPaper
            )
          "
        />
      </template>
    </div>
    <div v-if="selectedValidPapers.length">
      <div
        v-if="config.public.site.allowPurchase"
        class="grid gap-4 py-5 border-b border-gray-300"
      >
        <div class="flex flex-row items-center justify-between">
          <span class="font-sans text-base font-medium">{{
            $t('pricePerMonthLabel')
          }}</span>
          <span class="font-sans text-base font-medium">{{
            $formatCurrency(
              Math.ceil(
                (pricePerYear +
                  (selectedValidPapers.length - 1) *
                    pricePerYear *
                    (1 - discount)) /
                  12
              )
            )
          }}</span>
        </div>
        <div class="flex flex-row items-center justify-between">
          <span class="font-sans text-base font-medium">{{
            $t('pricePerYearLabel')
          }}</span>
          <span class="font-sans text-base font-medium">{{
            $formatCurrency(
              Math.round(
                pricePerYear +
                  (selectedValidPapers.length - 1) *
                    pricePerYear *
                    (1 - discount)
              )
            )
          }}</span>
        </div>
      </div>
      <div class="pt-4 grid gap-4">
        <CommonButton
          v-if="allowTrial"
          class="block w-full h-14"
          size="xxlarge"
          border="border-none"
          bg="bg-blue"
          text="text-white"
          text-hover="hover:text-white"
          bg-hover="hover:bg-denimblue"
          :disabled="!selectedValidPapers.length"
          @click.native="handleTrialSignupClick"
        >
          {{ $t('startTrial') }}
        </CommonButton>
        <template v-if="config.public.site.allowPurchase">
          <NuxtLink
            v-if="userStore.user && userStore.user.clientId"
            :to="{
              name: 'subscriptionCheckout',
              query: { papers: props.selectedValidPapers.join(',') },
            }"
          >
            <CommonButton
              class="block w-full h-14"
              size="xxlarge"
              :bg="allowTrial ? 'bg-gray-300' : 'bg-blue'"
              :text="allowTrial ? 'text-black' : 'text-white'"
              border="border-none"
              :bg-hover="
                allowTrial ? 'hover:bg-gray-200' : 'hover:bg-denimblue'
              "
              :text-hover="allowTrial ? 'hover:text-black' : 'hover:text-white'"
              :disabled="!selectedValidPapers.length"
              @click.native=""
            >
              {{ $t('PayNow') }}
            </CommonButton>
          </NuxtLink>
          <CommonButton
            v-else
            class="block w-full h-14"
            size="xxlarge"
            :bg="allowTrial ? 'bg-gray-300' : 'bg-blue'"
            :text="allowTrial ? 'text-black' : 'text-white'"
            border="border-none"
            :bg-hover="allowTrial ? 'hover:bg-gray-200' : 'hover:bg-denimblue'"
            :text-hover="allowTrial ? 'hover:text-black' : 'hover:text-white'"
            :disabled="!selectedValidPapers.length"
            @click.native="handleBuyNowClick"
          >
            {{ $t('PayNow') }}
          </CommonButton>
        </template>
        <CommonButton
          v-else
          class="block w-full h-14"
          size="xxlarge"
          :bg="allowTrial ? 'bg-gray-300' : 'bg-blue'"
          :text="allowTrial ? 'text-black' : 'text-white'"
          border="border-none"
          :bg-hover="allowTrial ? 'hover:bg-gray-200' : 'hover:bg-denimblue'"
          :text-hover="allowTrial ? 'hover:text-black' : 'hover:text-white'"
          :disabled="!selectedValidPapers.length"
          @click.native="$emit('showCallMeModal')"
        >
          {{ $t('GetContactedWithOffer') }}
        </CommonButton>
        <p
          v-if="
            selectedValidPapers.length >= 2 && config.public.site.allowPurchase
          "
          class="text-sm text-center text-gray-800"
        >
          * {{ $t('discountIsOnlyFirstYear') }}
        </p>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col items-center justify-center border-b border-gray-300 py-10 mb-5 gap-6"
    >
      <span class="font-sans font-normal text-gray-700 text-base">{{
        $t('youHaveNotChosenAnyPapersYet')
      }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ContentPaper } from '~/typesManual/content_api/paper'
import type { EcommerceItem } from '~/typesManual/dataLayer'

const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()
const userStore = useUserStore()
const dataLayer = useDataLayer()

const props = defineProps<{
  papers: ContentPaper[]
  selectedValidPapers: number[]
  selectedSortedValidPapers: number[]
  papersInWaitingPeriod: (number | undefined)[] | null
  removeSelectedPaper: (paper: ContentPaper) => void
}>()

const discount = 0.15
const pricePerYear = 5988

const allowTrial = computed(() => {
  // Checks if there is overlap between the selectedValidPapers and the papersInWaitingPeriod
  return props.selectedValidPapers.every(
    (paper) => !props.papersInWaitingPeriod?.includes(paper)
  )
})

const trialModalState = useTrialModalState()

function handleTrialSignupClick() {
  const returnUrl = new URL(window.location.origin)

  trialModalState.value.papers = props.selectedSortedValidPapers.map((paper) =>
    props.papers.find((p) => p.RecordId === paper)
  ) as ContentPaper[]
  trialModalState.value.open = true
  trialModalState.value.skipStartTrial = true
  trialModalState.value.returnUrl = returnUrl.href

  dataLayer.ecommerce.beginCheckout(
    props.selectedSortedValidPapers.map((paper) => ({
      item_name: props.papers.find((p) => p.RecordId === paper)?.Name ?? '',
      item_id: paper,
      price: Math.round(pricePerYear * (1 - discount)),
      quantity: 1,
    })) as EcommerceItem[]
  )
}

function handleBuyNowClick() {
  const returnUrl = new URL(window.location.origin)
  returnUrl.pathname = `/${nuxtApp.$t('Routes.Subscription')}/${nuxtApp.$t(
    'Routes.Payment'
  )}`
  returnUrl.searchParams.append('papers', props.selectedValidPapers.join(','))

  trialModalState.value.papers = []
  trialModalState.value.open = true
  trialModalState.value.skipStartTrial = true
  trialModalState.value.returnUrl = returnUrl.href
}
</script>
